/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Editor, { ActionComposerEditorArgs } from 'cio/components/action-composer/base-glimmer/editor';
import { InAppPreview } from 'cio/utils/previews';
import InAppMessage from 'cio/app-models/in-app/in-app-message';
import TemplateModel from 'cio/models/template';
import { inject as service } from '@ember/service';
import {
  InAppMessageConfig,
  InAppMessageConfigUpdate,
  MESSAGE_PRIORITY_LABELS,
  hasAbsoluteDateBeenSelectedOnDifferentTz,
  pageRuleSummary,
} from 'cio/utils/in-app/message-config';
import { action } from '@ember/object';
import { Framecast } from 'framecast';
import Ember from 'ember';
import {
  LOAD_TIMEOUT_MS,
  PARCEL_IN_APP_EDITOR_SRC,
  PARCEL_IN_APP_EDITOR_SRC_DEV,
  ParcelBroadcastEvent,
  ParcelState,
} from 'cio/utils/composer/parcel';
import { tracked } from '@glimmer/tracking';
import { didCancel, task, TaskInstance, timeout, waitForProperty } from 'ember-concurrency';
import { WritableAtom } from 'framecast';
import config from 'cio/config/environment';
import AlertsService from 'cio/services/alerts';

interface InAppParcelEditorArgs extends ActionComposerEditorArgs {
  preview: InAppPreview;
  isPreviewLoading: boolean;
  previewVariables: Record<string, unknown>;
  fetchPreview: () => TaskInstance<InAppPreview>;
  message: InAppMessage;
  template: TemplateModel;
  framecast: Framecast | null;
  generateHTML: () => Promise<void>;
  attachFramecast: (iframe: HTMLIFrameElement) => void;
  onMessageCartaContentChange: (content: string) => void;
  onMessageAttributeChange: (name: string, value: string) => void;
  onMessageConfigChange: (config: InAppMessageConfig) => void;
  onMessageTemplateChange: (messageId: string, attributes: string[]) => void;
  onRecipientChange: (recipient: string) => void;
  previewDidChange: () => void;
  isDirty: boolean;
  hasReadOnlyPermission: boolean;
}

interface InAppParcelEditorSignature {
  Args: InAppParcelEditorArgs;
  Element: HTMLDivElement;
}

export default class InAppParcelEditor extends Editor<InAppParcelEditorSignature> {
  @service alerts!: AlertsService;

  @tracked state: ParcelState = ParcelState.Init;
  @tracked expanded = false;
  @tracked showAbsoluteDateTooltip = false;

  content: WritableAtom<string> | null = null;
  variables: WritableAtom<Record<string, unknown>> | null = null;
  originalTemplateId = '';
  unsubscribeContentListener: (() => void) | null = null;

  constructor(owner: unknown, args: InAppParcelEditorSignature['Args']) {
    super(owner, args);
    this.originalTemplateId = args.template.id;
  }

  attachFramecast = task({ drop: true }, async (iframeEl: HTMLIFrameElement) => {
    if (Ember.testing) {
      return;
    }

    if (this.state !== ParcelState.Init) {
      return;
    }

    this.args.attachFramecast(iframeEl);
    this.setEditorStateAndEventListeners();
    this.loadTimeout.perform();
  });

  loadTimeout = task({ restartable: true }, async () => {
    await timeout(LOAD_TIMEOUT_MS);
    this.setEditorErrorState();
  });

  willDestroy() {
    super.willDestroy();
    this.teardownEditorEventListeners();
  }

  get framecast() {
    return this.args.framecast;
  }

  get parcelSrc() {
    if (config.isProduction) {
      return PARCEL_IN_APP_EDITOR_SRC;
    }
    return PARCEL_IN_APP_EDITOR_SRC_DEV;
  }

  get message() {
    return this.args.message;
  }

  get messageDisplayError() {
    let { display } = this.message;
    if (!display.hasErrors) {
      return '';
    }
    return display.errors[0];
  }

  get pageRuleSummary() {
    return pageRuleSummary(this.message.routeRules);
  }

  get expireDate() {
    let { preview } = this.args;
    return (preview as InAppPreview)?.body_json?.expiry;
  }

  get isPageRulesConfigured() {
    return this.message.routeRules.length > 0;
  }

  get priorityLabel() {
    return MESSAGE_PRIORITY_LABELS[this.message.priority];
  }

  get isLoading() {
    return this.state === ParcelState.Init;
  }

  get isError() {
    return this.state === ParcelState.Error;
  }

  @action
  setEditorErrorState() {
    this.state = ParcelState.Error;
  }

  @action
  setEditorReadyState() {
    void this.loadTimeout.cancelAll();

    this.state = ParcelState.Ready;

    this.setVariables();
    this.setContent();
  }

  @action
  onInAppMessageChange() {
    if (this.framecast == null) {
      return;
    }

    if (this.originalTemplateId !== this.args.template.id) {
      // template was changed (language switch).
      // listening on message change because
      // the content is updated after the message
      // is deserialized in parent component
      this.content?.set(this.message.cartaContent);
      this.originalTemplateId = this.args.template.id;
    }
  }

  @action
  setVariables() {
    if (this.variables == null) {
      return;
    }

    this.variables.set(this.args.previewVariables);
  }

  @action
  toggleExpanded() {
    this.expanded = !this.expanded;
  }

  @action
  setRecipient(recipient: string) {
    this.args.onRecipientChange(recipient);
  }

  @action
  setContent() {
    if (this.content == null) {
      return;
    }

    this.content.set(this.message.cartaContent);
  }

  @action
  onMessageConfigurationChange(config: InAppMessageConfigUpdate) {
    let { expiration, display, priority, routeRules, options, persistent } = config;

    this.showAbsoluteDateTooltip = hasAbsoluteDateBeenSelectedOnDifferentTz(options);

    this.args.onMessageConfigChange({ expiration, display, priority, routeRules, persistent });
  }

  @action
  private async onContentChange(content: string) {
    this.args.onMessageCartaContentChange(content);
  }

  @action
  private async framecastGetPreview() {
    try {
      await this.args.generateHTML();
      await (this.args.isPreviewLoading
        ? waitForProperty(this.args, 'isPreviewLoading', false)
        : this.args.fetchPreview());
    } catch (e) {
      if (!didCancel(e)) {
        throw e;
      }
    }

    await waitForProperty(this.args, 'preview.body_json', (json) => json != null);
    let body = this.args.preview?.body_json?.properties.gist.messageHtml ?? '';
    return { body };
  }

  @action
  private async framecastBroadcast({ event, data }: ParcelBroadcastEvent) {
    if (this.framecast == null) {
      return;
    }

    switch (event) {
      case 'ready':
        this.setEditorReadyState();
        break;
      case 'error':
        this.alerts.error('parcel.notification', data?.message ?? 'Something went wrong!');
        this.setEditorErrorState();
        break;
    }
  }

  private setEditorStateAndEventListeners() {
    if (this.framecast == null) {
      return;
    }

    this.content = this.framecast.state('content', this.message.cartaContent);
    this.variables = this.framecast.state('variables', this.args.previewVariables);
    this.unsubscribeContentListener = this.content.subscribe(this.onContentChange);
    this.framecast.on('function:getPreview', this.framecastGetPreview);
    this.framecast.on('broadcast', this.framecastBroadcast);
  }

  private teardownEditorEventListeners() {
    if (this.framecast == null) {
      return;
    }

    this.unsubscribeContentListener?.();
    this.framecast.off('broadcast', this.framecastBroadcast);
    this.framecast.off('function:getPreview', this.framecastGetPreview);
  }
}
